import { COOKIE_KEYS } from "../constants/cookie-key";

/**
 * Dữ liệu PHIÊN ĐĂNG NHẬP (`user`: token + thông tin nhân viên + danh sách quyền) KHÔNG lưu bằng cookie nữa
 * mà lưu ở localStorage, kèm hạn dùng như max-age cũ.
 *
 * Lý do: giá trị là nguyên JSON phản hồi login (có dấu ngoặc kép, dấu phẩy, khoảng trắng, chữ tiếng Việt có
 * dấu) và nặng vài KB (token còn chứa thêm một bản danh sách quyền + chi nhánh). Cookie như vậy vừa sai
 * chuẩn ký tự vừa sát giới hạn ~4 KB: Chrome vẫn nhận, còn Safari / mọi trình duyệt trên iPhone (WebKit) bỏ
 * cookie không báo lỗi → đăng nhập báo "thành công" rồi quay lại trang login.
 *
 * Mức an toàn không đổi: cookie cũ vốn không HttpOnly (JS phải đọc token để gắn Bearer), nên XSS đọc được
 * như nhau. Lợi thêm: token không còn bị gửi kèm mọi request tới host của web.
 */
const SESSION_STORAGE_KEYS: string[] = [COOKIE_KEYS.USER];
const isSessionKey = (name: string) => SESSION_STORAGE_KEYS.includes(name);

interface StoredSession {
  value: string;
  /** epoch ms — quá hạn thì coi như đã đăng xuất. */
  expires_at: number;
}

/** Đọc phiên từ localStorage. `undefined` = không có / hết hạn / localStorage không dùng được. */
function readSession(name: string): string | undefined {
  try {
    const raw = window.localStorage.getItem(name);
    if (!raw) return undefined;
    const stored = JSON.parse(raw) as StoredSession;
    if (!stored || typeof stored.value !== "string") return undefined;
    if (!(Number(stored.expires_at) > Date.now())) {
      window.localStorage.removeItem(name);
      return undefined;
    }
    return stored.value;
  } catch {
    return undefined;
  }
}

function writeSession(name: string, value: string, hours: number): boolean {
  try {
    const stored: StoredSession = { value, expires_at: Date.now() + hours * 60 * 60 * 1000 };
    window.localStorage.setItem(name, JSON.stringify(stored));
    return true;
  } catch {
    // localStorage bị chặn (chế độ riêng tư cũ, hết dung lượng…) → nơi gọi rơi về cookie như trước.
    return false;
  }
}

export default class CookieUtils {
  static getCookie<T = string>(name: string): T | null {
    if (isSessionKey(name)) {
      const value = readSession(name);
      if (value !== undefined) {
        try {
          return JSON.parse(value) as T;
        } catch {
          return value as unknown as T;
        }
      }
      // Không có ở localStorage → đọc tiếp cookie bên dưới: phiên đăng nhập TRƯỚC khi đổi cách lưu vẫn dùng
      // được tới khi hết hạn, không ai bị đăng xuất lúc deploy.
    }
    const nameEQ = name + "=";
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const trimmedCookie = cookie.trim();
      if (trimmedCookie.startsWith(nameEQ)) {
        const cookieValue = trimmedCookie.substring(nameEQ.length);

        // Nếu không có value, return null
        if (!cookieValue || cookieValue.trim() === "") {
          return null;
        }

        try {
          // Thử parse JSON trước (cho object/array hoặc string đã được stringify)
          return JSON.parse(cookieValue) as T;
        } catch {
          // Nếu không parse được, return raw value (cho plain string như URL)
          return cookieValue as unknown as T;
        }
      }
    }
    return null;
  }
  static eraseCookie(name: string): void {
    if (isSessionKey(name)) {
      try {
        window.localStorage.removeItem(name);
      } catch {
        /* localStorage không dùng được → chỉ còn cookie bên dưới */
      }
    }
    // document.cookie = `${name}=; Max-Age=-99999999; path=/; SameSite=Strict; Secure; HttpOnly`;
    document.cookie = `${name}=; Max-Age=-99999999; path=/;`;
  }

  static setCookie(name: string, value: string, hours: number = 12): void {
    if (isSessionKey(name) && writeSession(name, value, hours)) {
      // Dọn cookie cũ cùng tên (nếu còn) để không đọc nhầm dữ liệu phiên trước.
      document.cookie = `${name}=; Max-Age=-99999999; path=/;`;
      return;
    }
    const expires = `max-age=${hours * 60 * 60}`; // if days: (days * 24 * 60 * 60)
    // SameSite=Lax: hạn chế cookie bị gửi trong request cross-site (giảm rủi ro lộ/CSRF),
    // vẫn cho phép luồng điều hướng/đăng nhập hoạt động bình thường.
    let cookie = `${name}=${value}; ${expires}; path=/; SameSite=Lax`;
    // Secure: chỉ gửi cookie qua HTTPS. Chỉ thêm khi đang chạy HTTPS để không phá môi trường dev (http://localhost).
    if (typeof window !== "undefined" && window.location.protocol === "https:") {
      cookie += "; Secure";
    }
    // Lưu ý: KHÔNG đặt HttpOnly ở đây — cookie này do JS đọc/ghi (token gắn Bearer, privilege_codes ẩn/hiện menu).
    // Muốn HttpOnly thật sự thì token phải do BE set (xem Tầng 1 trong tài liệu phân quyền).
    document.cookie = cookie;
  }

  static getCurrentUser(): any {
    const user = this.getCookie<any>(COOKIE_KEYS.USER);
    return user ?? {};
  }

  static getRealtimeUrl(): string | null {
    return this.getCookie<string>("CONFIG_REALTIME_URL");
  }

  static getResourceUrl(): string | null {
    return this.getCookie<string>("CONFIG_RESOURCE_URL");
  }
}
